import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Divider } from '@chakra-ui/react';

import RunProgressPanel from 'components/run-progress/RunProgressPanel';
import { mapRunStepsToUi } from 'components/run-progress/mapRunStepsToUi';
import socketShipStationTagger from 'app/socketShipStationTagger';

import {
  getShipStationTaggerProcessStatusApi,
  downloadShipStationTaggerRunReportCsvApi,
} from 'features/third-party-integrations/third-party-integrations.api';
import { setShipStationTaggerProcessStatusFromSocket } from 'features/third-party-integrations/third-party-integrations.slice';
import { useColors } from './colors';

const TAGGER_SUMMARY_STATS = [
  { key: 'totalStores', label: 'Stores' },
  { key: 'succeededStores', label: 'Succeeded', color: 'green.500' },
  { key: 'failedStores', label: 'Failed', color: 'red.500' },
  { key: 'totalProcessedOrders', label: 'Orders processed' },
  { key: 'totalUpdatedOrders', label: 'Orders updated', color: 'green.500' },
];

const ShipStationTaggerProgress = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const { borderColor } = useColors();
  const {
    shipStationTaggerProcessStatus,
    isShipStationTaggerRunning,
    downloadShipStationTaggerReportStatus,
  } = useSelector((state) => state.thirdPartyIntegrations);

  const displayIsRunning =
    isShipStationTaggerRunning || Boolean(shipStationTaggerProcessStatus?.isRunning);
  const steps = useMemo(
    () => mapRunStepsToUi(shipStationTaggerProcessStatus),
    [shipStationTaggerProcessStatus],
  );
  const overallProgress = shipStationTaggerProcessStatus?.overallProgress ?? 0;
  const runSummary = shipStationTaggerProcessStatus?.runSummary ?? null;
  const hasReport = Boolean(shipStationTaggerProcessStatus?.hasReport);

  useEffect(() => {
    dispatch(getShipStationTaggerProcessStatusApi());
  }, [dispatch]);

  useEffect(() => {
    socketShipStationTagger.connect();
    const onRunState = (payload) => {
      dispatch(setShipStationTaggerProcessStatusFromSocket(payload));
    };
    const onStatus = () => {
      dispatch(getShipStationTaggerProcessStatusApi());
    };

    socketShipStationTagger.on('ship-station-tagger-run-state', onRunState);
    socketShipStationTagger.on('ship-station-tagger-status', onStatus);

    return () => {
      socketShipStationTagger.off('ship-station-tagger-run-state', onRunState);
      socketShipStationTagger.off('ship-station-tagger-status', onStatus);
      socketShipStationTagger.disconnect();
    };
  }, [dispatch]);

  useEffect(() => {
    if (!displayIsRunning) return undefined;
    const interval = setInterval(() => {
      dispatch(getShipStationTaggerProcessStatusApi());
    }, 2000);
    return () => clearInterval(interval);
  }, [dispatch, displayIsRunning]);

  const panel = (
    <RunProgressPanel
      title="Run progress"
      embedded
      steps={steps}
      overallProgress={overallProgress}
      isRunning={displayIsRunning}
      currentMessage={shipStationTaggerProcessStatus?.message ?? ''}
      runSummary={runSummary}
      summaryStats={TAGGER_SUMMARY_STATS}
      hasReport={hasReport}
      onDownloadReport={() => dispatch(downloadShipStationTaggerRunReportCsvApi())}
      isDownloadingReport={downloadShipStationTaggerReportStatus === 'loading'}
      showSummary={
        !displayIsRunning && runSummary && (runSummary.totalStores ?? 0) > 0
      }
    />
  );

  if (embedded) {
    return (
      <Box pt={2}>
        <Divider borderColor={borderColor} mb={6} />
        {panel}
      </Box>
    );
  }

  return (
    <RunProgressPanel
      title="Tagger run progress"
      steps={steps}
      overallProgress={overallProgress}
      isRunning={displayIsRunning}
      currentMessage={shipStationTaggerProcessStatus?.message ?? ''}
      runSummary={runSummary}
      summaryStats={TAGGER_SUMMARY_STATS}
      hasReport={hasReport}
      onDownloadReport={() => dispatch(downloadShipStationTaggerRunReportCsvApi())}
      isDownloadingReport={downloadShipStationTaggerReportStatus === 'loading'}
      showSummary={
        !displayIsRunning && runSummary && (runSummary.totalStores ?? 0) > 0
      }
    />
  );
};

export default ShipStationTaggerProgress;
